import * as React from "react";
import type { AdChannel } from "./types";
import { logTrackingDebug } from "../debug/debugLog";

type Bge = (...args: unknown[]) => void;

declare global {
  interface Window {
    bge?: Bge;
  }
}

/**
 * Where the pixel script loads from. BIGO's stock snippet lets a `?dm=` URL
 * param pick this domain instead, so anyone could send a link that loads their
 * own script on our page. We pin it.
 */
const BIGO_PIXEL_DOMAIN = "tk.mowell.tech";

function bge(...args: unknown[]): void {
  if (typeof window === "undefined") {
    return;
  }
  const [command, name, params] = args;
  logTrackingDebug("bigo", `${command} ${name}`, params);
  if (!window.bge) {
    logTrackingDebug("bigo", "bge missing: pixel script not on the page");
    return;
  }
  window.bge(...args);
}

/**
 * BIGO Ads pixel. The <head> snippet loads events.js and inits the pixel,
 * which sends page_view on each full page load. It also stores the `bbg` click
 * ID from the landing URL in the `_bge_bbg` cookie.
 *
 * Only the events set up on the pixel in BIGO Ads Manager are sent. BIGO has
 * no event ID to dedup on, so ec_purchase is sent only by lovelica-triggers
 * (Web Events API) from the real order.
 */
export function createBigoChannel(pixelID: string): AdChannel {
  const id = JSON.stringify(pixelID);
  const domain = JSON.stringify(BIGO_PIXEL_DOMAIN);

  return {
    name: "bigo",

    headComponents: () => [
      <script
        key="bigo-pixel"
        dangerouslySetInnerHTML={{
          __html: `window.bgdataLayer=window.bgdataLayer||[];function bge(){bgdataLayer.push(arguments)}window.bge=window.bge||bge;bge('init',${id});(function(e,t){try{try{localStorage.setItem("pixel_storage_dm",e)}catch(_){}var n=document.createElement("script");n.src="https://"+e+"/ad/events.js?pixel_id="+t;n.async=!0;n.setAttribute("data-pixel-script-dm",e);(document.head||document.documentElement).appendChild(n)}catch(_){}})(${domain},${id});`,
        }}
      />,
    ],

    // The pixel takes no user data.
    identify: () => {},

    track: (e) => {
      switch (e.name) {
        case "page-view":
          bge("event", "ec_detail_view");
          return;
        case "plan-selected":
          bge("event", "ec_add_cart", { monetary: e.properties.value, currency: e.properties.currency });
          return;
      }
    },
  };
}
